import { useState, useEffect } from 'react';
import { toaster } from 'evergreen-ui';
import { fetchTurniByMonth, saveTurno, updateTurno, fetchShiftRanges, saveShiftRange, updateShiftRange, deleteShiftRange, deleteTurno, timbraTurno } from '../../services/turniService';
import { useAuth } from '../../services/authContext';
import { fetchCollaborators } from '../../services/commonServices';
import { useRole } from '../../contexts/RoleContext';
import { isOvernightShift, buildOvernightSplit } from './overnightSplit';

export const EMPTY_RANGE = { batch_id: null, operator_name: '', from_date: '', to_date: '', start_time: '', end_time: '', shift_type: 'turno', is_overtime: false, shift_period: '', notes: '' };

const useTurni = ({ externalCollaborators = null } = {}) => {
  const { effectiveIsDirector: isDirector, isEducator } = useRole();
  const { user } = useAuth();
  const userName = user ? `${user.nome || ''} ${user.cognome || ''}`.trim() : 'Operatore';

  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [turniData, setTurniData] = useState([]);
  const [collaboratorsList, setCollaboratorsList] = useState([]);

  const [shiftRanges, setShiftRanges] = useState([]);
  const [rangeInputs, setRangeInputs] = useState(EMPTY_RANGE);

  const [shiftInputs, setShiftInputs] = useState({
    id: null,
    date: '',
    shift_type: 'turno',
    start_time: '',
    end_time: '',
    operator_name: userName,
    notes: '',
    is_overtime: false,
    shift_period: '',
  });

  const loadShiftRanges = async () => {
    try {
      const data = await fetchShiftRanges();
      setShiftRanges(data || []);
    } catch (e) {
      console.warn('Shift ranges non disponibili:', e.message);
    }
  };

  useEffect(() => {
    if (isDirector) loadShiftRanges();
    // loadShiftRanges è definita nello stesso scope e non cambia; includerla
    // causerebbe un loop (loadShiftRanges → setShiftRanges → re-render → nuovo effect)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirector]);

  useEffect(() => {
    if (Array.isArray(externalCollaborators) && externalCollaborators.length > 0) {
      setCollaboratorsList(externalCollaborators);
      return;
    }
    if (isDirector || isEducator) {
      fetchCollaborators()
        .then(data => {
          if (Array.isArray(data)) setCollaboratorsList(data);
        })
        .catch(err => console.error('Errore caricamento collaboratori', err));
    }
  }, [isDirector, isEducator, externalCollaborators]);

  const loadData = async () => {
    try {
      const data = await fetchTurniByMonth(selectedMonth);
      setTurniData(data || []);
    } catch (e) {
      toaster.danger('Errore durante il caricamento turni: ' + e.message);
    }
  };

  useEffect(() => {
    loadData();
    // loadData legge selectedMonth via closure; includerla come dep creerebbe
    // un loop (loadData → setTurniData → re-render → nuovo loadData)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth]);

  const emptyShift = () => ({ id: null, date: '', shift_type: 'turno', start_time: '', end_time: '', operator_name: userName, notes: '', is_overtime: false, shift_period: '' });

  const saveOvernightSplit = async (id) => {
    const { firstShift, secondShift } = buildOvernightSplit(shiftInputs);
    await (id ? updateTurno(firstShift) : saveTurno(firstShift));
    await saveTurno(secondShift);
    toaster.success(id ? 'Turno aggiornato e diviso su due giornate consecutive' : 'Turno salvato su due giornate consecutive');
  };

  const handleSaveTurno = async () => {
    try {
      const { id } = shiftInputs;

      if (isOvernightShift(shiftInputs)) {
        await saveOvernightSplit(id);
      } else if (id) {
        await updateTurno(shiftInputs);
        toaster.success('Turno aggiornato con successo');
      } else {
        await saveTurno(shiftInputs);
        toaster.success('Turno salvato con successo');
      }

      setShiftInputs(emptyShift());
      await loadData();
      return true;
    } catch (e) {
      toaster.danger('Errore: ' + e.message);
      return false;
    }
  };

  const handleDeleteTurno = async (id) => {
    try {
      await deleteTurno(id);
      toaster.success('Turno rimosso');
      await loadData();
    } catch (e) {
      toaster.danger('Errore rimozione: ' + e.message);
      throw e;
    }
  };

  const handleTimbra = async (shiftId, shiftDate) => {
    if (shiftDate > new Date().toISOString().split('T')[0]) {
      toaster.warning('Non è possibile timbrare un turno futuro');
      return;
    }
    try {
      await timbraTurno(shiftId);
      toaster.success('Turno timbrato!');
      await loadData();
    } catch (e) {
      toaster.danger('Errore timbratura: ' + e.message);
    }
  };

  const handleSaveShiftRange = async () => {
    try {
      if (rangeInputs.batch_id) {
        await updateShiftRange(rangeInputs);
        toaster.success('Intervallo aggiornato con successo');
      } else {
        await saveShiftRange(rangeInputs);
        toaster.success('Orario fisso assegnato con successo');
      }
      setRangeInputs(EMPTY_RANGE);
      await Promise.all([loadData(), loadShiftRanges()]);
      return true;
    } catch (e) {
      toaster.danger('Errore: ' + e.message);
      return false;
    }
  };

  const handleDeleteShiftRange = async (batch_id) => {
    try {
      await deleteShiftRange(batch_id);
      toaster.success('Intervallo eliminato');
      await Promise.all([loadData(), loadShiftRanges()]);
    } catch (e) {
      toaster.danger('Errore eliminazione intervallo: ' + e.message);
    }
  };

  const handleDragStart = (e, userNameStr) => {
    e.dataTransfer.setData('userName', userNameStr);
  };

  const handleDrop = (e, dateStr) => {
    e.preventDefault();
    if (!isDirector) return;
    const droppedName = e.dataTransfer.getData('userName');
    if (droppedName) {
      setShiftInputs(prev => ({ ...prev, id: null, operator_name: droppedName, date: dateStr, shift_type: 'turno' }));
      return droppedName;
    }
    return null;
  };

  return {
    userName,
    selectedMonth,
    setSelectedMonth,
    turniData,
    collaboratorsList,
    shiftInputs,
    setShiftInputs,
    loadData,
    handleSaveTurno,
    handleDeleteTurno,
    handleTimbra,
    handleDragStart,
    handleDrop,
    shiftRanges,
    rangeInputs,
    setRangeInputs,
    handleSaveShiftRange,
    handleDeleteShiftRange,
  };
};

export default useTurni;
